// Settlement position helpers (SM-797, SM-621 phase 09a). broker-service
// computes every figure; the portal renders them. A direction is always
// the wire's enum, said in words from the broker's side ("We owe you" /
// "You owe us"), and an amount is always a magnitude beside it — never a
// bare signed number the reader has to interpret.
import { PAYMENT_REQUEST_STATUSES } from "./types.ts";
import type { PaymentRequestStatus, SettlementPositionDirection } from "./types.ts";
import type { Tone } from "./payouts.ts";
import type { TranslationKey } from "../i18n/en.ts";

/** The windows the page offers, in days; the default is a recent operational window, not month-to-date. */
export const SETTLEMENT_WINDOWS = [1, 3, 7, 14, 30] as const;
export type SettlementWindowDays = (typeof SETTLEMENT_WINDOWS)[number];
export const SETTLEMENT_DEFAULT_DAYS: SettlementWindowDays = 7;

export function isSettlementWindow(v: unknown): v is SettlementWindowDays {
  return SETTLEMENT_WINDOWS.includes(Number(v) as SettlementWindowDays);
}

/**
 * The period [from, to) for "the last N days", ending at the next whole
 * minute — two reads a few seconds apart ask for the same period, so the
 * portal and backoffice show the same figures for the same window.
 */
export function settlementWindow(days: number, now = Date.now()): { from_unix_ms: number; to_unix_ms: number } {
  const minute = 60_000;
  const to = Math.ceil(now / minute) * minute;
  return { from_unix_ms: to - days * 24 * 60 * minute, to_unix_ms: to };
}

/**
 * How old the page's window may get before showing the tab again takes a
 * new `to` (SM-943, the backoffice's SETTLE_WINDOW_MAX_AGE_MS). Revenue and
 * clearing are read as of `to` but collateral and open requests live, so
 * re-reading an old `to` would mix the two.
 */
export const SETTLEMENT_WINDOW_MAX_AGE_MS = 60_000;

/** The page's settlement window, and what it was taken for and when. */
export interface HeldSettlementWindow {
  days: number;
  /** Everything else the window is read with (node, currency, scope): a change takes a new window. */
  reads: string;
  at: number;
  win: { from_unix_ms: number; to_unix_ms: number };
}

export function takeSettlementWindow(days: number, reads: string, at = Date.now()): HeldSettlementWindow {
  return { days, reads, at, win: settlementWindow(days, at) };
}

export function isSettlementWindowOld(held: HeldSettlementWindow, now = Date.now()): boolean {
  return now - held.at > SETTLEMENT_WINDOW_MAX_AGE_MS;
}

/** A new window when the held one is over a minute old, else the same object. */
export function renewSettlementWindowIfOld(held: HeldSettlementWindow, now = Date.now()): HeldSettlementWindow {
  return isSettlementWindowOld(held, now) ? takeSettlementWindow(held.days, held.reads, now) : held;
}

/**
 * Whether a focus refetch may read this query again (SM-943): only a read
 * at the held window's `to` while that window is young. An older window is
 * renewed when the tab is shown, under a new key, so re-reading the old `to`
 * would only fetch an answer the page throws away — or, kept, show the old
 * period.
 */
export function mayRefetchSettlementOnFocus(queryKey: readonly unknown[], held: HeldSettlementWindow, now = Date.now()): boolean {
  const to = queryKey.map((k) => (!!k && typeof k === "object" ? (k as { to_unix_ms?: unknown }).to_unix_ms : undefined)).find((v) => v !== undefined);
  return to === held.win.to_unix_ms && !isSettlementWindowOld(held, now);
}

/**
 * Whether the previous answer may stand in while a new settlement read
 * loads (SM-943): only for the same node and currency — a Period, scope or
 * renewed window. Another node's or currency's figures never do.
 */
export function keepsSettlementPlaceholder(prevKey: readonly unknown[] | undefined, nodeId: string, currency: string): boolean {
  if (!prevKey || prevKey[1] !== nodeId) return false;
  const p = prevKey[3];
  return !!p && typeof p === "object" && (p as { currency?: unknown }).currency === currency;
}

/**
 * The users' P&L split lines under the clearing (external credit) balance
 * (SM-885, PO-confirmed 2026-10-09): "Users' open positions" and "Users'
 * settled results and fees". Display only, parts of the clearing balance,
 * never added into any figure, drawn only when the server sends them. The
 * PO may ask to hide either after seeing it: flip its flag here.
 */
export const SETTLEMENT_SPLIT_LINES = {
  openPositions: true,
  settledResults: true,
} as const satisfies Record<string, boolean>;

/** |canonical| — the sign is the direction's job. */
export function magnitude(canon: string | undefined | null): string | undefined {
  if (!canon || !/^-?\d+$/.test(canon.trim())) return undefined;
  const v = canon.trim();
  return v.startsWith("-") ? v.slice(1) : v;
}

/**
 * What is left after collateral, as the "After collateral" stat and the
 * cover banner both show it (SM-912): uncovered, the part collateral does
 * not cover (transfer_in_required); covered, |uncovered| (= outstanding +
 * collateral) — never the collateral itself, which has its own stat.
 */
export function afterCollateral(f: { transfer_in_required: string; uncovered: string }): { covered: boolean; amount: string | undefined } {
  const covered = f.transfer_in_required === "0";
  return { covered, amount: covered ? magnitude(f.uncovered) : f.transfer_in_required };
}

/**
 * The users' open positions line's direction (SM-912): the server's, or —
 * from an older server that sends only the cost — the cost's side of
 * clearing_balance = users_settled_result − users_open_position_cost:
 * above 0 the broker owes us, at 0 settled.
 */
export function usersOpenPositionDirection(f: {
  users_open_position_cost?: string;
  users_open_position_direction?: SettlementPositionDirection;
}): SettlementPositionDirection {
  if (f.users_open_position_direction) return f.users_open_position_direction;
  const cost = f.users_open_position_cost?.trim() ?? "";
  if (!/^-?\d+$/.test(cost) || /^-?0+$/.test(cost)) return "SETTLED";
  return cost.startsWith("-") ? "WE_OWE_BROKER" : "BROKER_OWES_US";
}

// --- payment requests (SM-883 / SM-889) ---------------------------------------
//
// A request asks the broker for the external credit it owes, never the
// net; it is REQUESTED until linked confirmed INBOUND notes cover it (PAID)
// or the exchange cancels it with a reason (CANCELLED). Due dates are
// calendar days in Asia/Bangkok, as broker-service validates them
// (domain.SettlementLocation).

export const SETTLEMENT_TIME_ZONE = "Asia/Bangkok";

const bangkokDay = new Intl.DateTimeFormat("en-CA", {
  timeZone: SETTLEMENT_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** Today in Asia/Bangkok as YYYY-MM-DD (18:00 UTC is already the next day there). */
export function bangkokToday(now = Date.now()): string {
  const parts = Object.fromEntries(bangkokDay.formatToParts(new Date(now)).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/**
 * A REQUESTED request whose due date is before today in Asia/Bangkok. Due
 * today is not overdue; PAID and CANCELLED never are; no or a malformed
 * due date is never overdue.
 */
export function isOverdue(dueDate: string | undefined | null, status: string, now = Date.now()): boolean {
  if (status !== "REQUESTED" || !dueDate || !/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) return false;
  return dueDate < bangkokToday(now);
}

interface RequestStatusCopy {
  label: TranslationKey;
  tone: Tone;
}

const REQUEST_STATUSES: Record<PaymentRequestStatus, RequestStatusCopy> = {
  REQUESTED: { label: "settlement.requestStatus.REQUESTED", tone: "warn" },
  PAID: { label: "settlement.requestStatus.PAID", tone: "yes" },
  CANCELLED: { label: "settlement.requestStatus.CANCELLED", tone: "neutral" },
};

function isKnownRequestStatus(status: string): status is PaymentRequestStatus {
  return Object.prototype.hasOwnProperty.call(REQUEST_STATUSES, status);
}

/** The status label key; null for a status this build does not know (shown verbatim). */
export function paymentRequestStatusLabel(status: string): TranslationKey | null {
  return isKnownRequestStatus(status) ? REQUEST_STATUSES[status].label : null;
}

export function paymentRequestStatusTone(status: string): Tone {
  return isKnownRequestStatus(status) ? REQUEST_STATUSES[status].tone : "neutral";
}

export function paymentRequestStatuses(): readonly PaymentRequestStatus[] {
  return PAYMENT_REQUEST_STATUSES;
}

/**
 * A request's Outstanding cell: null ("—") for a CANCELLED request, which
 * asks for nothing whatever it received before it was cancelled (an older
 * server still sent max(0, amount − received) for it); otherwise the
 * server's outstanding_amount ("0" once PAID, SM-912).
 */
export function requestOutstanding(r: { status: string; outstanding_amount: string }): string | null {
  return r.status === "CANCELLED" ? null : r.outstanding_amount;
}

/**
 * The captions every request on the page carries — the list's caption,
 * shown once above it as the notes' caption is — and, per row, the rest.
 */
export function splitRequestCaptions(items: readonly { captions?: string[] }[]): { list: string[]; other: (captions?: string[]) => string[] } {
  const all = items.map((i) => i.captions ?? []);
  const list = all.length === 0 ? [] : all[0]!.filter((c) => all.every((cs) => cs.includes(c)));
  return { list, other: (captions) => (captions ?? []).filter((c) => !list.includes(c)) };
}

/**
 * The hint under the external credit owed, by the backoffice's rule
 * (SM-912, web-backoffice externalCreditHint; SM-941): a payment request
 * asks ONE node for its own figure, so under the subtree switch the card is
 * a node-and-branches total no request asks for, and for the node alone it
 * is what a request asks for only while the broker owes the exchange.
 * Nothing is owed that way otherwise, so no hint.
 */
export function externalCreditHintKey(
  f: { external_credit_direction?: SettlementPositionDirection },
  scope: "node" | "subtree",
): TranslationKey | undefined {
  if (scope === "subtree") return "settlement.externalCreditSubtree";
  return f.external_credit_direction === "BROKER_OWES_US" ? "settlement.externalCreditHint" : undefined;
}
